import React, { useState, useEffect, useRef } from 'react';
import { RotateCcw, AlertTriangle, Wand2 } from 'lucide-react';
import { getContrastRatio, fixContrastColor } from '../../utils/contrast';

export interface ColorPickerControlProps {
  label: string;
  description: string;
  value: string;
  defaultValue: string;
  onChange: (color: string) => void;
  contrastAgainstColor?: string | undefined;
  contrastAgainstLabel?: string | undefined;
  minRatio?: number | undefined;
}

export const ColorPickerControl: React.FC<ColorPickerControlProps> = ({
  label,
  description,
  value,
  defaultValue,
  onChange,
  contrastAgainstColor,
  contrastAgainstLabel,
  minRatio = 4.5,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isValidHex, setIsValidHex] = useState(true);
  const colorInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value);
    setIsValidHex(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value));
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value.trim();
    setInputValue(next);

    const formatted = next.startsWith('#') ? next : `#${next}`;
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(formatted)) {
      setIsValidHex(true);
      onChange(formatted);
    } else {
      setIsValidHex(false);
    }
  };

  const handleNativeColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value.toUpperCase();
    setInputValue(next);
    setIsValidHex(true);
    onChange(next);
  };

  const handleReset = () => {
    setInputValue(defaultValue);
    setIsValidHex(true);
    onChange(defaultValue);
  };

  const isDefault = value.toLowerCase() === defaultValue.toLowerCase();

  // Accessibility contrast check if configured
  const contrastRatio = contrastAgainstColor ? getContrastRatio(value, contrastAgainstColor) : null;
  const hasContrastIssue = contrastRatio !== null && contrastRatio < minRatio;

  const handleAutoFixContrast = () => {
    if (contrastAgainstColor) {
      const fixed = fixContrastColor(value, contrastAgainstColor, minRatio);
      setInputValue(fixed);
      setIsValidHex(true);
      onChange(fixed);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 rounded-2xl bg-card border border-border/80 hover:border-primary/30 transition-all gap-3">
      <div className="flex-1 min-w-0 pr-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-foreground truncate">{label}</span>
          {!isDefault && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-primary/10 text-primary font-semibold">
              Customized
            </span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{description}</p>

        {/* Contrast Warning Pill */}
        {hasContrastIssue && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-500 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded-xl">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">
              Low contrast ({contrastRatio}:1) with {contrastAgainstLabel || 'background'}
            </span>
            <button
              type="button"
              onClick={handleAutoFixContrast}
              className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-primary hover:underline bg-background/80 px-2 py-0.5 rounded-lg border border-primary/30 flex-shrink-0"
              title="Adjust color for AA compliance"
            >
              <Wand2 className="h-2.5 w-2.5" />
              Fix
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Color Swatch & Native Picker */}
        <div className="relative flex items-center">
          <button
            type="button"
            onClick={() => colorInputRef.current?.click()}
            className="w-8 h-8 rounded-xl border border-border/80 shadow-xs flex items-center justify-center transition hover:scale-105 overflow-hidden ring-1 ring-black/5"
            style={{ backgroundColor: isValidHex ? value : '#888888' }}
            title={`Pick ${label}`}
            aria-label={`Pick color for ${label}`}
          >
            <input
              ref={colorInputRef}
              type="color"
              value={isValidHex ? value : '#000000'}
              onChange={handleNativeColorChange}
              className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
            />
          </button>
        </div>

        {/* Hex Text Input */}
        <div className="relative">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="#000000"
            maxLength={7}
            className={`w-24 h-8 px-2.5 font-mono text-xs uppercase bg-muted/40 border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition ${
              isValidHex ? 'border-border' : 'border-destructive ring-1 ring-destructive'
            }`}
          />
        </div>

        {/* Reset / Use Default Button */}
        <button
          type="button"
          onClick={handleReset}
          disabled={isDefault}
          title={isDefault ? 'Using default color' : `Reset to default (${defaultValue})`}
          aria-label={`Reset ${label} to default`}
          className={`h-8 px-2.5 rounded-xl border text-[11px] font-semibold flex items-center gap-1 transition ${
            isDefault
              ? 'opacity-40 border-transparent text-muted-foreground cursor-default'
              : 'border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground hover:border-primary/30 shadow-xs'
          }`}
        >
          <RotateCcw className="h-3 w-3" />
          <span className="hidden sm:inline">Default</span>
        </button>
      </div>
    </div>
  );
};
